import { updateOrg } from '@/api/orgsAPI'
import { OrgData, OrgsGeneralInfo } from '@/models/Orgs'
import { RegionData } from '@/models/Regions'
import IwiPicker from '@/components/IwiPicker'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { useRegions, useRegionSets } from '@/utils/hooks/regions'
import { useIwi } from '@/utils/hooks/iwi'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  TextField,
  Typography,
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import Loading from './Loading'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import OrgGeneralInfoForm from './OrgGeneralInfoForm'
import { useOnboardingStatus } from '@/pages/onboarding/hooks/useOnboardingStatus'
import { useOnboardingData } from '@/utils/hooks/onboarding'
import { AccessibleTooltip } from './AccessibleTooltip'
import SpeechToText from './SpeechToText'
import { usePausedOrg } from '@/utils/hooks/usePausedOrg'
import LocationSelector from './LocationSelector'

interface Error {
  [key: string]: string
}

interface ProfileEditProps {
  organisation: OrgData
  token: string
  setSaved: React.Dispatch<React.SetStateAction<boolean>>
  // Which slice of the org form to render. 'details' = everything except
  // locations; 'locations' = only the location editor. Both share the same
  // formData + save path so no field is dropped regardless of which tab saves.
  section?: 'details' | 'locations'
}

export default function ProfileEdit({
  organisation,
  token,
  setSaved,
  section = 'details',
}: ProfileEditProps) {
  const { user, isLoading: userLoading } = getCurrentUser()
  const { regions, isLoading: regionsLoading } = useRegions(token)
  const { regionSets, isLoading: regionSetsLoading } = useRegionSets(token)
  const { iwi: iwiList, isLoading: iwiLoading } = useIwi(token)
  const [errors, setErrors] = useState<Error[]>([])
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState(false)
  const [showIwiInput, setShowIwiInput] = useState(
    organisation.iwi && organisation.iwi.length > 0,
  )
  const { isOnboarding } = useOnboardingStatus()
  const { data: onboardingData } = useOnboardingData({
    user_uuid: user?.uuid ?? null,
    token,
    selected_org_uuid: user?.selected_org_uuid ?? undefined,
  })
  const hasGeneratedProfile = onboardingData?.has_profile ?? false
  const [showDescriptionWarning, setShowDescriptionWarning] = useState(false)
  const [showLocationWarning, setShowLocationWarning] = useState(false)
  const { canWrite } = usePausedOrg()

  const [formData, setFormData] = useState({
    uuid: organisation.uuid,
    name: organisation.name,
    charities_number: organisation.charities_number,
    description: organisation.description,
    region_ids: organisation.region_ids || null,
    early_access: organisation.early_access,
    active: organisation.active,
    status: organisation.status,
    iwi: organisation.iwi,
    accepts_gambling_proceeds: organisation.accepts_gambling_proceeds,
    accepts_international: organisation.accepts_international,
  } as OrgData)

  const [generalFormData, setGeneralFormData] = useState({
    primary_contact_name: organisation.general_info?.primary_contact_name || '',
    primary_contact_phone:
      organisation.general_info?.primary_contact_phone || '',
    primary_contact_email:
      organisation.general_info?.primary_contact_email || '',
    secondary_contact_name:
      organisation.general_info?.secondary_contact_name || '',
    secondary_contact_phone:
      organisation.general_info?.secondary_contact_phone || '',
    secondary_contact_email:
      organisation.general_info?.secondary_contact_email || '',
    physical_address: organisation.general_info?.physical_address || '',
    postal_address: organisation.general_info?.postal_address || '',
    website: organisation.general_info?.website || '',
  } as OrgsGeneralInfo)

  const queryClient = useQueryClient()

  useEffect(() => {
    if (
      formData.name !== organisation.name ||
      formData.charities_number !== organisation.charities_number ||
      formData.description !== organisation.description ||
      formData.region_ids !== organisation.region_ids ||
      formData.early_access !== organisation.early_access ||
      formData.active !== organisation.active ||
      formData.status !== organisation.status ||
      formData.iwi !== organisation.iwi ||
      formData.accepts_gambling_proceeds !==
        organisation.accepts_gambling_proceeds ||
      formData.accepts_international !== organisation.accepts_international
    ) {
      setSuccess(false)
      setSaved(false)
      setErrors([])
    }
  }, [formData, organisation])

  async function handleSubmit(
    e: React.MouseEvent<HTMLButtonElement>,
    force: boolean = false,
    forceLocation: boolean = false,
  ) {
    e.preventDefault()
    setErrors([])
    setSaving(true)
    setSuccess(false)
    setSaved(false)

    if (token) {
      if (formData.region_ids) {
        formData.region_ids = formData.region_ids.map((region_id) =>
          Number(region_id),
        )
      }

      // Description rules only apply on the details tab (where it's editable).
      if (section === 'details') {
        if (!formData.description) {
          setErrors([{ formSubmit: 'Please enter a description' }])
          setSaving(false)
          return
        }

        // char count without spaces
        const textLength = formData.description.replace(/\s+/g, '').length

        if (textLength < 500 && !force) {
          setShowDescriptionWarning(true)
          setSaving(false)
          return
        }
      }

      // National-only is valid (nationally available funding is always
      // included), but confirm before saving with no local regions — either an
      // empty selection or only the legacy "New Zealand" region id.
      const onlyNationalSelected =
        !formData.region_ids ||
        formData.region_ids.length === 0 ||
        (formData.region_ids.length === 1 &&
          regions
            ?.find((r: RegionData) => r.id === formData.region_ids![0])
            ?.name?.toLowerCase() === 'new zealand')
      if (section === 'locations' && !forceLocation && onlyNationalSelected) {
        setShowLocationWarning(true)
        setSaving(false)
        return
      }

      if (
        section === 'details' &&
        showIwiInput &&
        (!formData.iwi || formData.iwi.length === 0)
      ) {
        setSaving(false)
        setErrors((prevErrors) => [
          ...prevErrors,
          {
            formSubmit:
              'Please select at least one iwi or uncheck the iwi funding option',
          },
        ])
        return
      }

      if (
        formData.charities_number &&
        formData.charities_number.trim() === ''
      ) {
        formData.charities_number = null
      } // set charity number to null if empty

      if (
        section === 'details' &&
        formData.charities_number &&
        !/CC\d+/.test(formData.charities_number)
      ) {
        setSaving(false)
        setErrors((prevErrors) => [
          ...prevErrors,
          { formSubmit: 'Invalid charities number' },
        ])
      }

      const response = await updateOrg({
        token,
        editedOrg: { ...formData, general_info: generalFormData },
      })

      if (response && 'error' in response) {
        setSaving(false)
        setSaved(false)
        const duplicate = response.duplicate
        const message = !duplicate
          ? 'Sorry something went wrong'
          : duplicate.alreadyMember
            ? `You're already a member of ${duplicate.orgName}.`
            : `An organisation named ${duplicate.orgName} already exists on Fundsorter. If this is a different organisation, please use a more specific name. If it's the same organisation, please contact help@fundsorter.com to join it.`
        setErrors((prevErrors) => [...prevErrors, { formSubmit: message }])
      } else if (response) {
        setSuccess(true)
        setSaved(true)
        setSaving(false)
        setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: ['currentOrgs', user?.uuid],
          })
        }, 100)
      } else {
        setSaving(false)
        setSaved(false)
        setErrors((prevErrors) => [
          ...prevErrors,
          { formSubmit: 'Sorry something went wrong' },
        ])
      }
    }
  }

  async function handleForceSubmit(e: React.MouseEvent<HTMLButtonElement>) {
    setShowDescriptionWarning(false)
    await handleSubmit(e, true, false) // force description, check location
  }

  async function handleForceLocationSubmit(
    e: React.MouseEvent<HTMLButtonElement>,
  ) {
    setShowLocationWarning(false)
    await handleSubmit(e, true, true) // force both
  }

  if (userLoading || regionsLoading || regionSetsLoading || iwiLoading) {
    return <Loading />
  }

  return (
    <>
      {section === 'details' && showDescriptionWarning && (
        <Dialog
          open={showDescriptionWarning}
          onClose={() => setShowDescriptionWarning(false)}
          aria-labelledby="warning-dialog-title"
          aria-describedby="warning-dialog-description"
        >
          <DialogTitle id="warning-dialog-title">
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img
                src={`/Thumbs-down400px.png`}
                alt="Thumbs down"
                style={{
                  width: '40px',
                  height: '40px',
                  marginRight: '15px',
                  marginLeft: '0px',
                  marginTop: '10px',
                }}
              />
              <Typography variant="h6" sx={{ mt: 1 }}>
                Your description is looking a little lean!
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <DialogContentText
              id="warning-dialog-description"
              sx={{
                color: '#1C3045',
                mt: 1,
              }}
            >
              Add more information to your description to get better funding
              matches.
            </DialogContentText>

            <Typography variant="body2" sx={{ color: '#1C3045', mt: 2, mb: 1 }}>
              Some ideas for what to include:
            </Typography>

            <Typography variant="body2" sx={{ color: '#1C3045', ml: 2 }}>
              • Mission and Vision
            </Typography>
            <Typography variant="body2" sx={{ color: '#1C3045', ml: 2 }}>
              • Community Engagement and Impact
            </Typography>
            <Typography variant="body2" sx={{ color: '#1C3045', ml: 2 }}>
              • Objectives
            </Typography>
            <Typography variant="body2" sx={{ color: '#1C3045', ml: 2 }}>
              • Programs and Services
            </Typography>
            <Typography variant="body2" sx={{ color: '#1C3045', ml: 2 }}>
              • Notable Achievements
            </Typography>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={(e) => handleForceSubmit(e)}
              variant="text"
              disableRipple
              sx={{
                textTransform: 'none',
                width: '150px',
                '&:focus': {
                  backgroundColor: '#e8e8e8',
                },
              }}
            >
              Save and continue
            </Button>

            <Button
              onClick={() => setShowDescriptionWarning(false)}
              variant="contained"
              disableRipple
              sx={{
                color: 'white',
                bgcolor: '#1C3045',
                textTransform: 'none',
                fontSize: '16px',
                width: '150px',
              }}
            >
              Keep editing
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {section === 'locations' && showLocationWarning && (
        <Dialog
          open={showLocationWarning}
          onClose={() => setShowLocationWarning(false)}
          aria-labelledby="location-warning-dialog-title"
          aria-describedby="location-warning-dialog-description"
        >
          <DialogTitle id="location-warning-dialog-title">
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ mt: 1 }}>
                Are you sure you're a national organisation?
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <DialogContentText
              id="location-warning-dialog-description"
              sx={{
                color: '#1C3045',
                mt: 1,
              }}
            >
              You haven't added any local regions. Nationally available funding
              is always included, but if your organisation works in specific
              towns, cities, or regions, you'll miss out on local funding
              opportunities.
            </DialogContentText>

            <Typography variant="body2" sx={{ color: '#1C3045', mt: 2, mb: 1 }}>
              Adding specific locations helps you get matched with:
            </Typography>

            <Typography variant="body2" sx={{ color: '#1C3045', ml: 2 }}>
              • Local council grants
            </Typography>
            <Typography variant="body2" sx={{ color: '#1C3045', ml: 2 }}>
              • Regional community funding
            </Typography>
            <Typography variant="body2" sx={{ color: '#1C3045', ml: 2 }}>
              • Place-based initiatives
            </Typography>
            <Typography variant="body2" sx={{ color: '#1C3045', ml: 2 }}>
              • Local trust funding
            </Typography>

            <Typography
              variant="body2"
              sx={{ color: '#1C3045', mt: 2, fontStyle: 'italic' }}
            >
              Don't worry — adding specific locations won't remove national
              opportunities. You'll still see everything available for New
              Zealand.
            </Typography>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={(e) => handleForceLocationSubmit(e)}
              variant="text"
              disableRipple
              sx={{
                textTransform: 'none',
                width: '200px',
                '&:focus': {
                  backgroundColor: '#e8e8e8',
                },
              }}
            >
              Yes, we're national only
            </Button>

            <Button
              onClick={() => setShowLocationWarning(false)}
              variant="contained"
              disableRipple
              sx={{
                color: 'white',
                bgcolor: '#1C3045',
                textTransform: 'none',
                fontSize: '16px',
                width: '150px',
              }}
            >
              Add locations
            </Button>
          </DialogActions>
        </Dialog>
      )}
      <FormControl fullWidth>
        {section === 'locations' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              margin: '0 auto',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
              <FormLabel htmlFor="regions">Locations</FormLabel>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                The areas where your organisation works. You'll be matched with
                all funding within the councils you've confirmed, including
                suburb-specific opportunities.{' '}
                <strong>
                  Nationally available funding opportunities are always
                  included.
                </strong>
              </Typography>
              {(!formData.region_ids || formData.region_ids.length === 0) && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignSelf: 'flex-start',
                    alignItems: 'center',
                    bgcolor: '#fff3cd',
                    color: '#856404',
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                    fontSize: '14px',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    ⚠️ Specifying location(s) will improve the quality of
                    matches
                  </Typography>
                </Box>
              )}
            </Box>

            <LocationSelector
              regions={regions ?? []}
              regionSets={regionSets ?? []}
              selectedIds={formData.region_ids ?? []}
              onChange={(ids) => setFormData({ ...formData, region_ids: ids })}
              mode="settings"
            />
          </Box>
        )}
        {section === 'details' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              margin: '0 auto',
            }}
          >
            <FormLabel htmlFor="orgName" sx={{ mt: 3 }}>
              Organisation Name *
            </FormLabel>
            <TextField
              type="text"
              id="orgName"
              name="orgName"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              inputProps={{
                style: { backgroundColor: '#fff' },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#757575',
                },
              }}
            />

            <FormLabel htmlFor="charitiesNumber" sx={{ mt: 2 }}>
              Charities Number
            </FormLabel>
            <TextField
              type="text"
              id="charitiesNumber"
              name="charitiesNumber"
              value={formData.charities_number || ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  charities_number: e.target.value,
                })
              }
              required
              inputProps={{
                style: { backgroundColor: '#fff' },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#757575',
                },
              }}
            />

            <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showIwiInput}
                    onChange={(e) => {
                      setShowIwiInput(e.target.checked)
                      if (!e.target.checked) {
                        setFormData({ ...formData, iwi: [] })
                      }
                    }}
                    sx={{
                      '&.MuiCheckbox-colorPrimary': {
                        color: '#1C3045',
                      },
                    }}
                  />
                }
                label="Potentially eligible for iwi funding"
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ ml: 4, mb: 1 }}
              >
                Select this option if you wish to be matched with opportunities
                available for specific iwi. You can select multiple iwi.
              </Typography>
            </Box>

            {showIwiInput && (
              <IwiPicker
                iwiList={iwiList}
                iwiLoading={iwiLoading}
                selectedIds={formData.iwi ?? []}
                onChange={(ids) => setFormData({ ...formData, iwi: ids })}
              />
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!formData.accepts_gambling_proceeds}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          accepts_gambling_proceeds: !e.target.checked,
                        })
                      }
                      sx={{
                        '&.MuiCheckbox-colorPrimary': {
                          color: '#1C3045',
                        },
                      }}
                      key={'accepts_gambling_proceeds'}
                    />
                  }
                  label="Refuse Gambling Proceeds"
                />
                <AccessibleTooltip title="Proceeds from Lotteries, Pokies, and Casinos">
                  <span
                    role="img"
                    aria-label="Info: Proceeds from Lotteries, Pokies, and Casinos"
                    tabIndex={0}
                  >
                    <HelpOutlineIcon
                      sx={{ fontSize: '20px', ml: -1, color: 'gray' }}
                    />
                  </span>
                </AccessibleTooltip>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 10 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.accepts_international}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          accepts_international: e.target.checked,
                        })
                      }
                      sx={{
                        '&.MuiCheckbox-colorPrimary': {
                          color: '#1C3045',
                        },
                      }}
                      key={'accepts_international'}
                    />
                  }
                  label="Accept International Funding"
                />
                <AccessibleTooltip title="Funding Opportunities from Outside of New Zealand">
                  <span
                    role="img"
                    aria-label="Info: Funding Opportunities from Outside of New Zealand"
                    tabIndex={0}
                  >
                    <HelpOutlineIcon
                      sx={{ fontSize: '20px', ml: -1, color: 'gray' }}
                    />
                  </span>
                </AccessibleTooltip>
              </Box>
            </Box>
            <FormLabel htmlFor="orgDescription" sx={{ mt: 2 }}>
              Organisation Description
            </FormLabel>
            <Typography variant="body2" color="textSecondary" mb={2}>
              Most importantly we want to know your organisations Mission and
              Vision, Community Engagement and Impact, and your Programmes and
              Services.
              <br />
              You can also let us know any other relevant information that might
              be useful to help us understand your organisation such as:
              Objective and Strategic Focus, Funding and Resources, Notable
              Achievements.
            </Typography>

            <Box>
              <SpeechToText
                token={token}
                onTranscription={(text) =>
                  setFormData({
                    ...formData,
                    description: formData.description
                      ? `${formData.description}\n\n${text}`
                      : text,
                  })
                }
              />
              <TextField
                type="text"
                id="orgDescription"
                name="orgDescription"
                value={formData.description || ''}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                multiline
                rows={4}
                fullWidth
                inputProps={{
                  style: {
                    backgroundColor: '#fff',
                    overflowWrap: 'break-word',
                    wordWrap: 'break-word',
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                  '& .MuiOutlinedInput-input': {
                    backgroundColor: '#fff',
                    overflowWrap: 'break-word',
                    wordWrap: 'break-word',
                    resize: 'vertical',
                  },
                }}
              />
            </Box>
          </Box>
        )}
        {section === 'details' && (
          <OrgGeneralInfoForm
            generalFormData={generalFormData}
            setGeneralFormData={setGeneralFormData}
          />
        )}
        {isOnboarding && hasGeneratedProfile && (
          <Alert
            severity="info"
            sx={{
              mt: 2,
            }}
          >
            Remember to regenerate your Funding Profile after you've made any
            changes to your Name, Description, or Locations so that your profile
            is up to date.
          </Alert>
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            position: 'relative',
            mt: 2,
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
              alignItems: 'flex-end',
            }}
          >
            <Button
              onClick={(e) => handleSubmit(e, false)}
              sx={{
                width: '200px',
                bgcolor: '#1C3045',
                fontSize: '16px',
                textTransform: 'none',
                borderRadius: 1,
                '&:hover': {
                  bgcolor: '#132336',
                },

                mb: errors.length > 0 || success ? 0 : 10,
              }}
              variant="contained"
              disableRipple
              disabled={!formData.name || saving || !canWrite}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>

            {errors.length > 0 ? (
              <Alert
                severity="error"
                sx={{
                  mt: 1,
                  textAlign: 'center',
                  borderRadius: 1,
                  width: '100%',
                  mb: 10,
                }}
              >
                {errors[0].formSubmit}
              </Alert>
            ) : success ? (
              <Alert
                severity="success"
                sx={{
                  mt: 2,
                  textAlign: 'center',
                  borderRadius: 1,
                  width: '100%',
                  maxWidth: '300px',
                }}
              >
                Profile saved!
              </Alert>
            ) : null}
          </Box>
        </Box>
      </FormControl>
    </>
  )
}
